/**
 * How guessable a new password is — 0 (trivially) to 4 (very hard).
 *
 * zxcvbn-ts with its language-neutral common-password dictionary, the pair
 * Clerk's own sign-up uses: it recognises common passwords, keyboard walks,
 * repeats, dates and l33t substitutions — what a length rule cannot see — and
 * the person's own name and email when passed as `userInputs`. It only ADVISES:
 * the rule (`passwordSchema`, @tonnie/schemas) and Clerk's breached-password
 * check are what refuse a password.
 *
 * The estimator is loaded on first use (a lazy chunk, kept once loaded), so
 * pages without a new-password field never download it; `null` until it has
 * scored the current password.
 *
 * Platform-neutral on purpose — React and zxcvbn only — so the citizen app
 * scores a new password with the same estimator and the same bands as the web
 * (it imports this file by its own subpath, `@tonnie/ui/lib/use-password-strength`).
 */
import type { ZxcvbnFactory } from '@zxcvbn-ts/core';
import { useEffect, useState } from 'react';

export type PasswordStrengthScore = 0 | 1 | 2 | 3 | 4;

/** The word a score is shown with — one banding for every screen that shows it. */
export type PasswordStrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

export function passwordStrengthLevel(score: PasswordStrengthScore): PasswordStrengthLevel {
  if (score <= 1) return 'weak';
  if (score === 2) return 'fair';
  if (score === 3) return 'good';
  return 'strong';
}

const SCORE_DELAY_MS = 120;

let estimator: Promise<ZxcvbnFactory> | null = null;

function loadEstimator(): Promise<ZxcvbnFactory> {
  estimator ??= Promise.all([import('@zxcvbn-ts/core'), import('@zxcvbn-ts/language-common')]).then(
    ([core, common]) =>
      new core.ZxcvbnFactory({
        dictionary: { ...common.dictionary },
        graphs: common.adjacencyGraphs,
        useLevenshteinDistance: true,
      })
  );
  return estimator;
}

export function usePasswordStrength(
  password: string,
  userInputs: readonly string[] = []
): PasswordStrengthScore | null {
  const [score, setScore] = useState<PasswordStrengthScore | null>(null);
  const inputsKey = userInputs.join('\u0000');

  useEffect(() => {
    if (!password) {
      setScore(null);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      void loadEstimator().then((zxcvbn) => {
        if (current) {
          setScore(zxcvbn.check(password, inputsKey ? inputsKey.split('\u0000') : []).score);
        }
      });
    }, SCORE_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [password, inputsKey]);

  return score;
}
